import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { Dialog } from "@/components/ui/Dialog";
import { AutoResizeTextarea } from "@/components/ui/AutoResizeTextarea";
import { GdDocumentLangtextField } from "@/components/ui/GdRichTextarea";
import { Button } from "@/components/ui/Button";
import { GermanDecimalInput, TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  betragAnteilEinzelpreis,
  formatCurrency,
  formatQuantityInput,
  formatQuantityWithUnit,
  normalizeKalkPreisbezug,
  normalizeRabattAusweis,
  positionEpNetto,
  rabattIsActive,
  unitForCustomer,
  type KalkPreisbezug,
} from "@/lib/documentCalc";
import { ConfirmActionDialog } from "@/pages/montageplanung/ConfirmActionDialog";
import { getKundenStundenArtikel, type GdLohnart } from "@/lib/dokumente";
import {
  GdPositionDocPriceCard,
  GdPositionDocSection,
  GdPositionInternalSection,
  GdPositionVisibilityLegend,
} from "@/pages/dokumente/DocumentPositionFieldSections";
import { DocumentArticleField } from "@/pages/dokumente/DocumentArticleField";
import { PositionAufschlagFields } from "@/pages/dokumente/PositionAufschlagFields";
import { PositionRabattFields } from "@/pages/dokumente/PositionRabattFields";
import { UnitAutocompleteField } from "@/pages/dokumente/UnitAutocompleteField";
import {
  calcLeistungAufschlagBetragNetto,
  calcLeistungKalkuliertNetto,
  calcLeistungZwischensummeNetto,
  patchLeistungPauschale,
  resolveLeistungVkDriver,
  resolveLeistungVkNetto,
} from "@/pages/dokumente/documentLeistungPricing";
import { kalkulierterVk } from "@/pages/dokumente/documentPricing";
import { KalkSumRow, KalkSumStack } from "@/pages/dokumente/KalkSumStack";
import {
  applyLeistungFieldsToArtikelZeile,
  applyArtikelInterneNotizFromQuelle,
  applyArtikelPricingFromDatabase,
  applyLeistungArtikelPricingFromDatabase,
  applyLieferscheinDelivery,
  applyStundenArtikelToNachweisPosition,
  calcLeistungArtikelZeileNetto,
  calcLeistungEinkaufNetto,
  calcLeistungLohnNetto,
  calcLeistungLohnZeileNetto,
  calcLeistungMaterialNetto,
  clearArtikeldatenForStammSwitch,
  hasArtikeldatenToClearForStammSwitch,
  DEFAULT_NACHWEIS_HINWEIS,
  ensurePositionMediaKey,
  getArtikelNachkalk,
  getPositionMeta,
  isArtikelEpFromDatabase,
  isFreierArtikel,
  isLeistungArtikelEpFromDatabase,
  isNachweisPosition,
  newLeistungArtikel,
  newLeistungLohnFromArt,
  normalizePositionTyp,
  patchArtikelPricingByAufschlag,
  patchArtikelPricingByAufschlagModus,
  patchArtikelPricingByEk,
  patchArtikelPricingByVk,
  patchPositionMeta,
  positionBlockLabel,
  positionTotals,
  readLieferscheinDelivery,
  resolvePositionPreisart,
  switchArtikelToFreierArtikel,
  syncLeistungArtikelPricing,
  syncLeistungEinzelpreis,
  toNachweisArtikelPosition,
  type EditorPosition,
  type LeistungArtikelZeile,
  type LeistungKalkulation,
  type LeistungLohnZeile,
} from "@/pages/dokumente/documentPositionModel";
import type { GdPositionsart, GdPreisart } from "@/pages/dokumente/gdPositionMeta";
import { DocumentPositionImages } from "@/pages/dokumente/DocumentPositionImages";

const PREISBEZUG_OPTIONS: { value: KalkPreisbezug; label: string; title: string }[] = [
  { value: "einzel", label: "Einzel", title: "Gilt je Stück der Position" },
  { value: "gesamt", label: "Gesamt", title: "Gilt einmal für die ganze Menge" },
];

function PreisbezugControl({
  value,
  disabled,
  onChange,
}: {
  value: unknown;
  disabled?: boolean;
  onChange: (next: KalkPreisbezug) => void;
}) {
  return (
    <SegmentedControl
      name="Preisbezug"
      value={normalizeKalkPreisbezug(value)}
      onChange={onChange}
      disabled={disabled}
      options={PREISBEZUG_OPTIONS.map((opt) => ({
        value: opt.value,
        label: <span title={opt.title}>{opt.label}</span>,
      }))}
    />
  );
}

function KalkZeileSumme({
  betrag,
  preisbezug,
  positionsMenge,
  currency,
}: {
  betrag: number;
  preisbezug: unknown;
  positionsMenge: number;
  currency: string;
}) {
  const gesamt = normalizeKalkPreisbezug(preisbezug) === "gesamt";
  const anteil = betragAnteilEinzelpreis(betrag, preisbezug, positionsMenge);
  return (
    <div className="text-right">
      <div>{formatCurrency(betrag, currency)}</div>
      {gesamt ? (
        <div className="gd-kalk-anteil">Anteil EP {formatCurrency(anteil, currency)}</div>
      ) : null}
    </div>
  );
}

interface DocumentPositionEditDialogProps {
  open: boolean;
  position: EditorPosition | null;
  editable: boolean;
  currency: string;
  lohnarten: GdLohnart[];
  /** Lokale kunden.id für Standard-Stundenartikel bei Umschaltung auf Nachweis. */
  kundenId?: number | null;
  /** Projekt-ID: bevorzugt Hero-Kunden-Mapping über auftraege.hero_customer_id. */
  auftragId?: number | null;
  /** Dokument-ID für Positionsbilder. */
  docId?: number | null;
  /** Nur im Angebot: optionale / alternative Positionen markieren. */
  dokumentTyp?: string | null;
  onClose: () => void;
  onSave: (position: EditorPosition) => void;
  /** Nach Upload/Löschen von Positionsbildern (z. B. Editor-Karten aktualisieren). */
  onAttachmentsChanged?: () => void;
  zIndexClass?: string;
}

export function DocumentPositionEditDialog({
  open,
  position,
  editable,
  currency,
  lohnarten,
  kundenId,
  auftragId,
  docId = null,
  dokumentTyp = null,
  onClose,
  onSave,
  onAttachmentsChanged,
  zIndexClass,
}: DocumentPositionEditDialogProps) {
  const [draft, setDraft] = useState<EditorPosition | null>(position);
  const [confirmStammSwitch, setConfirmStammSwitch] = useState(false);
  const openedLocalIdRef = useRef<string | null>(null);
  const [preisOrientierung, setPreisOrientierung] = useState<{
    orientierungspreis_netto: number;
    preis_gueltig_bis?: string | null;
  } | null>(null);
  const [leistungOrientierung, setLeistungOrientierung] = useState<
    Record<string, { orientierungspreis_netto: number; preis_gueltig_bis?: string | null }>
  >({});

  useEffect(() => {
    if (!open) {
      openedLocalIdRef.current = null;
      setConfirmStammSwitch(false);
      setPreisOrientierung(null);
      setLeistungOrientierung({});
      return;
    }
    const localId = position?.localId ?? null;
    if (localId !== openedLocalIdRef.current) {
      openedLocalIdRef.current = localId;
      setConfirmStammSwitch(false);
      setPreisOrientierung(null);
      setLeistungOrientierung({});
      if (position && isNachweisPosition(position)) {
        setDraft(toNachweisArtikelPosition(position));
      } else {
        setDraft(position);
      }
    }
  }, [open, position]);

  const active = open ? draft ?? position : null;
  const typ = active ? normalizePositionTyp(active.position_typ) : "artikel";
  const isNachweis = active ? isNachweisPosition(active) : false;
  const showPreisartToggle = typ === "artikel" || typ === "leistung" || isNachweis;
  const preisartValue: GdPreisart =
    active && resolvePositionPreisart(active) === "nachweis" ? "nachweis" : "festpreis";
  const showPositionsartToggle =
    dokumentTyp === "angebot" && (typ === "artikel" || typ === "leistung" || isNachweis);
  const isLieferscheinQty =
    dokumentTyp === "lieferschein" &&
    (typ === "artikel" || typ === "leistung" || isNachweis);
  const delivery = active && isLieferscheinQty ? readLieferscheinDelivery(active) : null;

  const preview = useMemo(() => {
    if (!active) return null;
    const synced = typ === "leistung" ? syncLeistungEinzelpreis(active) : active;
    return positionTotals(synced);
  }, [active, typ]);

  const leistungKalk = (active?.kalkulation_json || {
    artikel_liste: [],
    lohn_liste: [],
  }) as LeistungKalkulation;

  const artikelNachkalk = active ? getArtikelNachkalk(active) : {};
  const positionMeta = active ? getPositionMeta(active) : {};
  const positionsartValue: "standard" | GdPositionsart =
    positionMeta.positionsart === "optional" || positionMeta.positionsart === "alternativ"
      ? positionMeta.positionsart
      : "standard";

  function patch(partial: Partial<EditorPosition>) {
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  function patchLeistung(partial: Partial<LeistungKalkulation>) {
    setDraft((prev) => {
      if (!prev) return prev;
      const current = (prev.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
      return syncLeistungEinzelpreis({
        ...prev,
        kalkulation_json: { ...current, ...partial },
      });
    });
  }

  function patchLeistungArtikel(index: number, partial: Partial<LeistungArtikelZeile>) {
    setDraft((prev) => {
      if (!prev) return prev;
      const current = (prev.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
      const list = [...(current.artikel_liste || [])];
      const row = list[index];
      if (!row) return prev;
      list[index] = { ...row, ...partial };
      return syncLeistungEinzelpreis({
        ...prev,
        kalkulation_json: { ...current, artikel_liste: list },
      });
    });
  }

  function patchLeistungLohn(index: number, partial: Partial<LeistungLohnZeile>) {
    setDraft((prev) => {
      if (!prev) return prev;
      const current = (prev.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
      const list = [...(current.lohn_liste || [])];
      const row = list[index];
      if (!row) return prev;
      list[index] = { ...row, ...partial };
      return syncLeistungEinzelpreis({
        ...prev,
        kalkulation_json: { ...current, lohn_liste: list },
      });
    });
  }

  function patchMeta(partial: Parameters<typeof patchPositionMeta>[1]) {
    setDraft((prev) => (prev ? patchPositionMeta(prev, partial) : prev));
  }

  function patchDelivery(update: {
    beauftragt?: number;
    geliefert?: number;
    rueckstandEntfernt?: boolean;
  }) {
    setDraft((prev) => (prev ? applyLieferscheinDelivery(prev, update) : prev));
  }

  function renderQuantityFields(options?: {
    includeBezeichnung?: boolean;
    bezeichnungLabel?: string;
  }) {
    const includeBezeichnung = Boolean(options?.includeBezeichnung);
    const bezeichnungLabel = options?.bezeichnungLabel || "Bezeichnung";
    if (delivery && active) {
      const rueckstand = delivery.rueckstand_menge;
      return (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3 sm:items-end">
            <TextInput
              label="Beauftragt"
              type="number"
              className="gd-pos-menge-field"
              value={formatQuantityInput(delivery.beauftragte_menge)}
              onChange={(e) => patchDelivery({ beauftragt: Number(e.target.value) })}
              disabled={!editable}
            />
            <TextInput
              label="Geliefert"
              type="number"
              className="gd-pos-menge-field"
              value={formatQuantityInput(delivery.geliefert_menge)}
              onChange={(e) => patchDelivery({ geliefert: Number(e.target.value) })}
              disabled={!editable}
            />
            <div className="space-y-1 text-sm">
              <span className="text-label-secondary">Rückstand</span>
              <div className="flex min-h-[2.5rem] items-center justify-between gap-2 rounded-md border border-separator bg-fill-quaternary/30 px-3 py-2">
                <span className="font-medium tabular-nums text-label">
                  {rueckstand > 0.0005
                    ? formatQuantityWithUnit(rueckstand, active.einheit)
                    : "—"}
                </span>
                {editable && rueckstand > 0.0005 ? (
                  <button
                    type="button"
                    className="shrink-0 text-xs text-status-red underline-offset-2 hover:underline"
                    onClick={() => patchDelivery({ rueckstandEntfernt: true })}
                  >
                    Löschen
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={
              includeBezeichnung
                ? "grid gap-3 sm:grid-cols-[minmax(6.5rem,8rem)_1fr] sm:items-end"
                : "grid gap-3 sm:grid-cols-[minmax(6.5rem,8rem)] sm:items-end"
            }
          >
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Einheit</span>
              <UnitAutocompleteField
                value={active.einheit || (isNachweis ? "Std." : "")}
                onChange={(value) =>
                  patch({ einheit: value || (isNachweis ? "Std." : "") })
                }
                disabled={!editable}
              />
            </label>
            {includeBezeichnung ? (
              <TextInput
                label={bezeichnungLabel}
                value={active.bezeichnung}
                onChange={(e) => patch({ bezeichnung: e.target.value })}
                disabled={!editable}
              />
            ) : null}
          </div>
        </div>
      );
    }

    if (includeBezeichnung) {
      return (
        <div className="grid gap-3 sm:grid-cols-[minmax(4.5rem,5.5rem)_minmax(6.5rem,8rem)_1fr] sm:items-end">
          <TextInput
            label="Menge"
            type="number"
            className="gd-pos-menge-field"
            value={formatQuantityInput(active?.menge ?? 0)}
            onChange={(e) => patch({ menge: Number(e.target.value) })}
            disabled={!editable}
          />
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Einheit</span>
            <UnitAutocompleteField
              value={active?.einheit || (isNachweis ? "Std." : "")}
              onChange={(value) =>
                patch({ einheit: value || (isNachweis ? "Std." : "") })
              }
              disabled={!editable}
            />
          </label>
          <TextInput
            label={bezeichnungLabel}
            value={active?.bezeichnung || ""}
            onChange={(e) => patch({ bezeichnung: e.target.value })}
            disabled={!editable}
          />
        </div>
      );
    }

    return (
      <div className="grid gap-3 sm:grid-cols-[minmax(4.5rem,5.5rem)_1fr] sm:items-end">
        <TextInput
          label="Menge"
          type="number"
          className="gd-pos-menge-field"
          value={formatQuantityInput(active?.menge ?? 0)}
          onChange={(e) => patch({ menge: Number(e.target.value) })}
          disabled={!editable}
        />
        <label className="block space-y-1 text-sm">
          <span className="text-label-secondary">Einheit</span>
          <UnitAutocompleteField
            value={active?.einheit || ""}
            onChange={(value) => patch({ einheit: value })}
            disabled={!editable}
          />
        </label>
      </div>
    );
  }

  function setPreisart(preisart: GdPreisart) {
    if (preisart === "nachweis") {
      setDraft((prev) => {
        if (!prev) return prev;
        return toNachweisArtikelPosition(
          patchPositionMeta(prev, {
            preisart: "nachweis",
            nachweis_hinweis:
              getPositionMeta(prev).nachweis_hinweis || DEFAULT_NACHWEIS_HINWEIS,
          }),
        );
      });
      // Leere Nachweispositionen mit Kunden-Stundenartikel vorbelegen
      void (async () => {
        try {
          const raw = await getKundenStundenArtikel(kundenId ?? null, {
            auftragId: auftragId ?? null,
          });
          setDraft((prev) => {
            if (!prev || !isNachweisPosition(prev)) return prev;
            return applyStundenArtikelToNachweisPosition(prev, {
              artikel_id: raw.artikel_id ?? null,
              hero_nr: raw.hero_nr ?? null,
              bezeichnung: raw.bezeichnung ?? null,
              einheit: raw.einheit ?? "Std.",
              einzelpreis_netto: raw.einzelpreis_netto ?? null,
              rabatt_prozent: raw.rabatt_prozent ?? null,
            });
          });
        } catch {
          // Mapping optional – Nutzer wählt Artikel manuell
        }
      })();
      return;
    }
    setDraft((prev) => (prev ? patchPositionMeta(prev, { preisart: "festpreis" }) : prev));
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      if (position && isNachweisPosition(position)) {
        setDraft(toNachweisArtikelPosition(position));
      } else {
        setDraft(position);
      }
      return;
    }
    onClose();
  }

  function handleSave() {
    if (!draft || !editable) return;
    const next = isNachweisPosition(draft)
      ? toNachweisArtikelPosition(draft)
      : typ === "leistung"
        ? syncLeistungEinzelpreis(draft)
        : draft;
    onSave(next);
    onClose();
  }

  if (!active) return null;

  const artikelKalkVk = kalkulierterVk(artikelNachkalk, active.einzelpreis_netto);
  const artikelVkDiff = Number(active.einzelpreis_netto ?? 0) - artikelKalkVk;
  const artikelEpFromDatabase = isArtikelEpFromDatabase(active);

  const leistungKalkLive = (syncLeistungEinzelpreis(active).kalkulation_json ||
    leistungKalk) as LeistungKalkulation;
  const leistungVkNetto = resolveLeistungVkNetto(
    leistungKalkLive,
    active.einzelpreis_netto,
    active.menge,
  );
  const leistungKalkuliertNetto = calcLeistungKalkuliertNetto(leistungKalkLive, active.menge);
  const leistungVkDiff = leistungVkNetto - leistungKalkuliertNetto;
  const leistungVkDriver = resolveLeistungVkDriver(leistungKalkLive);
  const leistungPauschaleAktiv =
    leistungVkDriver === "pauschale" && Number(leistungKalk.pauschale_netto || 0) > 0;
  const leistungPositionNetto = preview?.zeilen_netto ?? 0;
  const leistungEpImDokument = unitForCustomer(
    leistungVkNetto,
    Number(active.menge ?? 1),
    active,
  );
  const artikelEpImDokument = positionEpNetto(active);
  const rabattSichtbar = normalizeRabattAusweis(active.rabatt_ausweis) === "dokument" && rabattIsActive(active);

  return (
    <>
    <Dialog
      open={open}
      onClose={() => handleOpenChange(false)}
      title={
        <span className="inline-flex items-center gap-2">
          <span>{`${editable ? "Position bearbeiten" : "Position ansehen"} · ${active ? positionBlockLabel(active) : "Position"}`}</span>
          {auftragId ? (
            <ProjectOverviewButton auftragId={auftragId} className="h-7 w-7 shrink-0" />
          ) : null}
        </span>
      }
      size="4xl"
      fullScreenMobile
      zIndexClass={zIndexClass}
      dirty={editable}
      footer={
        editable ? (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave}>
              Übernehmen
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
              Schließen
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-5">
        <GdPositionVisibilityLegend />

        {showPreisartToggle ? (
          <div className="space-y-1">
            <span className="text-sm text-label-secondary">Preisart (nur intern)</span>
            <SegmentedControl
              value={preisartValue}
              onChange={(value) => setPreisart(value as GdPreisart)}
              disabled={!editable}
              options={[
                { value: "festpreis", label: "Festpreis" },
                { value: "nachweis", label: "Nachweis" },
              ]}
            />
            <p className="text-xs text-label-tertiary">
              Der Badge erscheint auf der Positionskarte, nicht im Kundendokument.
            </p>
          </div>
        ) : null}

        {showPositionsartToggle ? (
          <div className="space-y-1">
            <span className="text-sm text-label-secondary">Positionsart</span>
            <SegmentedControl
              value={positionsartValue}
              onChange={(value) => {
                const next = value as "standard" | GdPositionsart;
                patchMeta({
                  positionsart: next === "standard" ? undefined : next,
                });
              }}
              disabled={!editable}
              options={[
                { value: "standard", label: "Standard" },
                { value: "optional", label: "Optional" },
                { value: "alternativ", label: "Alternativ" },
              ]}
            />
            <p className="text-xs text-label-tertiary">
              Optional: erscheint im Angebot und zählt zur Gesamtsumme. Alternativ: erscheint im
              Angebot, zählt nicht zur Summe. Bei der Auftragsbestätigung entscheiden Sie, ob der
              Kunde die Position wollte.
            </p>
          </div>
        ) : null}

        {isNachweis ? (
          <GdPositionDocSection hint="Hinweis und Stunden-Artikel erscheinen im Dokument. Es gibt keine interne Nachkalkulation.">
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Hinweis oberhalb der Position</span>
              <AutoResizeTextarea
                value={positionMeta.nachweis_hinweis ?? DEFAULT_NACHWEIS_HINWEIS}
                onChange={(e) => patchMeta({ nachweis_hinweis: e.target.value })}
                disabled={!editable}
                minRows={2}
                placeholder={DEFAULT_NACHWEIS_HINWEIS}
              />
              <span className="text-xs text-label-tertiary">
                Wird im Angebot oberhalb der Position angezeigt.
              </span>
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Stunden-Artikel</span>
              <DocumentArticleField
                value={{
                  bezeichnung: active.bezeichnung,
                  artikel_id: active.artikel_id,
                  hero_nr: active.hero_nr,
                  einheit: active.einheit || "Std.",
                  ist_freier_artikel: active.ist_freier_artikel,
                }}
                disabled={!editable}
                stundenOnly
                onApply={(result) => {
                  setDraft((prev) => {
                    if (!prev) return prev;
                    return applyArtikelInterneNotizFromQuelle(
                      toNachweisArtikelPosition({
                        ...prev,
                        bezeichnung: result.bezeichnung,
                        artikel_id: result.artikel_id,
                        hero_nr: result.hero_nr,
                        einheit: "Std.",
                        ist_freier_artikel: result.ist_freier_artikel,
                        position_typ: "artikel",
                        einzelpreis_netto:
                          result.einzelpreis_netto ?? prev.einzelpreis_netto,
                        rabatt_prozent: result.rabatt_prozent ?? prev.rabatt_prozent,
                      }),
                      result.interne_beschreibung,
                    );
                  });
                }}
              />
            </label>
            {renderQuantityFields({ includeBezeichnung: true })}
            <GdDocumentLangtextField
              label="Beschreibung / Langtext"
              value={active.beschreibung || ""}
              onChange={(value) => patch({ beschreibung: value })}
              disabled={!editable}
            />
            <GdPositionDocPriceCard>
              <div className="grid gap-3 sm:grid-cols-3 sm:items-start">
                <TextInput
                  label="EP netto"
                  type="number"
                  value={String(active.einzelpreis_netto)}
                  onChange={(e) => patch({ einzelpreis_netto: Number(e.target.value) })}
                  disabled={!editable}
                  hint="Verkaufspreis aus der Artikeldatenbank — erscheint im Dokument."
                />
                <PositionRabattFields
                  value={active}
                  disabled={!editable}
                  onChange={(partial) => patch(partial)}
                />
                <TextInput
                  label="MwSt %"
                  type="number"
                  value={String(active.steuersatz_prozent)}
                  onChange={(e) => patch({ steuersatz_prozent: Number(e.target.value) })}
                  disabled={!editable}
                />
              </div>
              {rabattIsActive(active) ? (
                <div className="gd-pos-doc-price__ep mt-2">
                  <span className="gd-pos-doc-price__ep-label">
                    {rabattSichtbar ? "EP netto im Dokument" : "EP netto nach Rabatt"}
                  </span>
                  <span className="gd-pos-doc-price__ep-value">
                    {formatCurrency(artikelEpImDokument, currency)}
                  </span>
                </div>
              ) : null}
              {preview ? (
                <div className="gd-pos-doc-price__totals">
                  <div className="gd-pos-doc-price__total-row">
                    <span>Zeile netto</span>
                    <span>{formatCurrency(preview.zeilen_netto, currency)}</span>
                  </div>
                  <div className="gd-pos-doc-price__total-row gd-pos-doc-price__total-row--grand">
                    <span>Zeile brutto</span>
                    <span>{formatCurrency(preview.zeilen_brutto, currency)}</span>
                  </div>
                </div>
              ) : null}
            </GdPositionDocPriceCard>
          </GdPositionDocSection>
        ) : null}

        {typ === "artikel" && !isNachweis ? (
          <>
            <GdPositionDocSection hint="Bezeichnung, Menge, Preis und Langtext erscheinen in der Positionsliste.">
              <SegmentedControl
                value={isFreierArtikel(active) ? "frei" : "stamm"}
                onChange={(value) => {
                  if (value === "frei") {
                    setDraft((prev) => (prev ? switchArtikelToFreierArtikel(prev) : prev));
                    return;
                  }
                  if (!isFreierArtikel(active)) {
                    patch({ ist_freier_artikel: false });
                    return;
                  }
                  if (hasArtikeldatenToClearForStammSwitch(active)) {
                    setConfirmStammSwitch(true);
                    return;
                  }
                  setDraft((prev) => (prev ? clearArtikeldatenForStammSwitch(prev) : prev));
                }}
                disabled={!editable}
                options={[
                  { value: "stamm", label: "Artikeldatenbank" },
                  { value: "frei", label: "Freier Artikel" },
                ]}
              />
              {!isFreierArtikel(active) ? (
                <label className="block space-y-1 text-sm">
                  <span className="text-label-secondary">Artikel</span>
                  <DocumentArticleField
                    value={{
                      bezeichnung: active.bezeichnung,
                      artikel_id: active.artikel_id,
                      hero_nr: active.hero_nr,
                      einheit: active.einheit,
                      ist_freier_artikel: active.ist_freier_artikel,
                    }}
                    disabled={!editable}
                    onApply={(result) => {
                      if (
                        result.preis_abgelaufen &&
                        result.orientierungspreis_netto != null &&
                        result.orientierungspreis_netto > 0
                      ) {
                        setPreisOrientierung({
                          orientierungspreis_netto: result.orientierungspreis_netto,
                          preis_gueltig_bis: result.preis_gueltig_bis,
                        });
                      } else {
                        setPreisOrientierung(null);
                      }
                      setDraft((prev) => {
                        if (!prev) return prev;
                        let next: EditorPosition = {
                          ...prev,
                          bezeichnung: result.bezeichnung,
                          artikel_id: result.artikel_id,
                          hero_nr: result.hero_nr,
                          einheit: result.einheit ?? prev.einheit,
                          ist_freier_artikel: result.ist_freier_artikel,
                          position_typ: "artikel",
                          rabatt_prozent: result.rabatt_prozent ?? prev.rabatt_prozent,
                        };
                        if (
                          result.einkaufspreis_netto != null &&
                          result.einzelpreis_netto != null
                        ) {
                          next = applyArtikelPricingFromDatabase(
                            next,
                            result.einkaufspreis_netto,
                            result.einzelpreis_netto,
                            { abgelaufen: Boolean(result.preis_abgelaufen) },
                          );
                        } else if (result.einzelpreis_netto != null) {
                          next = { ...next, einzelpreis_netto: result.einzelpreis_netto };
                        }
                        return applyArtikelInterneNotizFromQuelle(next, result.interne_beschreibung);
                      });
                    }}
                  />
                </label>
              ) : (
                <TextInput
                  label="Bezeichnung"
                  value={active.bezeichnung}
                  onChange={(e) => patch({ bezeichnung: e.target.value })}
                  disabled={!editable}
                />
              )}
              {preisOrientierung ? (
                <p className="text-xs text-status-orange">
                  Alter Preis (Orientierung):{" "}
                  {formatCurrency(preisOrientierung.orientierungspreis_netto, currency)}
                  {preisOrientierung.preis_gueltig_bis
                    ? `, gültig bis ${new Date(
                        `${preisOrientierung.preis_gueltig_bis.slice(0, 10)}T12:00:00`,
                      ).toLocaleDateString("de-DE")}`
                    : ""}{" "}
                  – fließt nicht in die Berechnung.
                </p>
              ) : null}
              {renderQuantityFields()}
              <GdDocumentLangtextField
                label="Beschreibung / Langtext"
                value={active.beschreibung || ""}
                onChange={(value) => patch({ beschreibung: value })}
                disabled={!editable}
              />
              <GdPositionDocPriceCard>
                <div className="grid gap-3 sm:grid-cols-3 sm:items-start">
                  <TextInput
                    label="EP netto (Verkaufspreis)"
                    type="number"
                    value={String(active.einzelpreis_netto)}
                    onChange={(e) =>
                      setDraft((prev) =>
                        prev ? patchArtikelPricingByVk(prev, Number(e.target.value)) : prev,
                      )
                    }
                    disabled={!editable || artikelEpFromDatabase}
                    hint={
                      preisOrientierung
                        ? "Abgelaufener Artikelpreis → 0 €; bitte ggf. manuell setzen. Alter Preis nur Orientierung."
                        : artikelEpFromDatabase
                          ? "Aus Artikeldatenbank übernommen — Änderung über EK/Aufschlag in der Nachkalkulation"
                          : undefined
                    }
                  />
                  <PositionRabattFields
                    value={active}
                    disabled={!editable}
                    onChange={(partial) => patch(partial)}
                  />
                  <TextInput
                    label="MwSt %"
                    type="number"
                    value={String(active.steuersatz_prozent)}
                    onChange={(e) => patch({ steuersatz_prozent: Number(e.target.value) })}
                    disabled={!editable}
                  />
                </div>
                {rabattIsActive(active) ? (
                  <div className="gd-pos-doc-price__ep mt-2">
                    <span className="gd-pos-doc-price__ep-label">
                      {rabattSichtbar ? "EP netto im Dokument" : "EP netto nach Rabatt"}
                    </span>
                    <span className="gd-pos-doc-price__ep-value">
                      {formatCurrency(artikelEpImDokument, currency)}
                    </span>
                    <span className="gd-pos-doc-price__ep-hint">
                      Erscheint so als EP in Positionsliste und Dokument
                    </span>
                  </div>
                ) : null}
                {preview ? (
                  <div className="gd-pos-doc-price__totals">
                    <div className="gd-pos-doc-price__total-row">
                      <span>Zeile netto</span>
                      <span>{formatCurrency(preview.zeilen_netto, currency)}</span>
                    </div>
                    <div className="gd-pos-doc-price__total-row gd-pos-doc-price__total-row--grand">
                      <span>Zeile brutto</span>
                      <span>{formatCurrency(preview.zeilen_brutto, currency)}</span>
                    </div>
                  </div>
                ) : null}
              </GdPositionDocPriceCard>
            </GdPositionDocSection>

            <GdPositionInternalSection
              title="Nachkalkulation"
              hint="Einkaufspreis und Aufschlag dienen der internen Kalkulation. Der Verkaufspreis oben erscheint im Dokument."
            >
              <div className="gd-artikel-nachkalk-grid">
                <TextInput
                  label="EK netto"
                  type="number"
                  value={String(artikelNachkalk.einkaufspreis_netto ?? "")}
                  onChange={(e) =>
                    setDraft((prev) =>
                      prev
                        ? patchArtikelPricingByEk(
                            prev,
                            e.target.value === "" ? 0 : Number(e.target.value),
                          )
                        : prev,
                    )
                  }
                  disabled={!editable}
                />
                <PositionAufschlagFields
                  value={artikelNachkalk}
                  disabled={!editable}
                  onValueChange={(amount) =>
                    setDraft((prev) => (prev ? patchArtikelPricingByAufschlag(prev, amount) : prev))
                  }
                  onModusChange={(modus) =>
                    setDraft((prev) =>
                      prev ? patchArtikelPricingByAufschlagModus(prev, modus) : prev,
                    )
                  }
                />
              </div>
              <KalkSumStack className="gd-artikel-nachkalk-summary">
                <KalkSumRow label="Kalkulierter VK netto" value={artikelKalkVk} currency={currency} />
                <KalkSumRow
                  label="EP im Dokument"
                  value={Number(active.einzelpreis_netto ?? 0)}
                  currency={currency}
                  bold
                  separator
                />
                {Math.abs(artikelVkDiff) >= 0.005 ? (
                  <KalkSumRow
                    label="Abweichung"
                    value={artikelVkDiff}
                    currency={currency}
                    accent
                  />
                ) : null}
              </KalkSumStack>
              <p className="gd-nachkalk-section__hint">
                Beim Artikel aus der Datenbank wird der Hero-VK automatisch übernommen. Änderungen an
                EK oder Aufschlag berechnen den kalkulierten VK — der EP im Dokument kann davon
                abweichen.
              </p>
            </GdPositionInternalSection>
          </>
        ) : null}

        {typ === "leistung" && !isNachweis ? (
          <>
            <GdPositionDocSection hint="Bezeichnung, Menge und Langtext erscheinen in der Positionsliste. Der Einzelpreis ergibt sich aus der internen Kalkulation.">
              {isLieferscheinQty ? (
                renderQuantityFields({
                  includeBezeichnung: true,
                  bezeichnungLabel: "Leistungsbezeichnung",
                })
              ) : (
                <div className="grid gap-3 sm:grid-cols-[minmax(4.5rem,5.5rem)_minmax(6.5rem,8rem)_1fr] sm:items-end">
                  <TextInput
                    label="Menge"
                    type="number"
                    className="gd-pos-menge-field"
                    value={formatQuantityInput(active.menge)}
                    onChange={(e) => patch({ menge: Number(e.target.value) })}
                    disabled={!editable}
                  />
                  <label className="block space-y-1 text-sm">
                    <span className="text-label-secondary">Einheit</span>
                    <UnitAutocompleteField
                      value={active.einheit || ""}
                      onChange={(value) => patch({ einheit: value })}
                      disabled={!editable}
                    />
                  </label>
                  <TextInput
                    label="Leistungsbezeichnung"
                    value={active.bezeichnung}
                    onChange={(e) => patch({ bezeichnung: e.target.value })}
                    disabled={!editable}
                  />
                </div>
              )}
              <GdDocumentLangtextField
                label="Beschreibung / Langtext"
                value={active.beschreibung || ""}
                onChange={(value) => patch({ beschreibung: value })}
                disabled={!editable}
              />
              <GdPositionDocPriceCard>
                <div className="gd-pos-doc-price__ep">
                  <span className="gd-pos-doc-price__ep-label">EP netto im Dokument</span>
                  <span className="gd-pos-doc-price__ep-value">
                    {formatCurrency(leistungEpImDokument, currency)}
                  </span>
                  {rabattIsActive(active) ? (
                    <span className="gd-pos-doc-price__ep-hint">
                      {rabattSichtbar
                        ? "Rabatt wird auf der Rechnung ausgewiesen"
                        : `${formatCurrency(leistungVkNetto, currency)} vor Rabatt`}
                      {!rabattSichtbar && leistungVkDriver === "pauschale" ? " · über Pauschale" : ""}
                    </span>
                  ) : leistungVkDriver === "pauschale" ? (
                    <span className="gd-pos-doc-price__ep-hint">Über Pauschale festgelegt</span>
                  ) : (
                    <span className="gd-pos-doc-price__ep-hint">Aus Kalkulation übernommen</span>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 sm:items-start">
                  <PositionRabattFields
                    value={active}
                    disabled={!editable}
                    onChange={(partial) => patch(partial)}
                  />
                  <TextInput
                    label="MwSt %"
                    type="number"
                    value={String(active.steuersatz_prozent)}
                    onChange={(e) => patch({ steuersatz_prozent: Number(e.target.value) })}
                    disabled={!editable}
                  />
                </div>
                <div className="gd-pos-doc-price__totals">
                  <div className="gd-pos-doc-price__total-row">
                    <span>
                      Position netto (× {formatQuantityInput(active.menge)} {active.einheit || "Stk"})
                    </span>
                    <span>{formatCurrency(leistungPositionNetto, currency)}</span>
                  </div>
                  {preview ? (
                    <div className="gd-pos-doc-price__total-row gd-pos-doc-price__total-row--grand">
                      <span>Position brutto</span>
                      <span>{formatCurrency(preview.zeilen_brutto, currency)}</span>
                    </div>
                  ) : null}
                </div>
              </GdPositionDocPriceCard>
            </GdPositionDocSection>

            <GdPositionInternalSection
              title="Lohn"
              hint="Stunden und Sätze fließen in die interne Kalkulation ein. Einzel gilt je Stück, Gesamt einmal für die ganze Menge — z. B. 1 h TB bei 200 Stück, ohne vorher zu teilen."
            >
              <div className="gd-pos-internal-block-head">
                <p className="text-xs text-label-secondary">
                  Summe Lohn im EP: {formatCurrency(calcLeistungLohnNetto(leistungKalk, active.menge), currency)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {editable && lohnarten.length ? (
                    lohnarten.map((art) => (
                      <Button
                        key={art.id}
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          patchLeistung({
                            lohn_liste: [...(leistungKalk.lohn_liste || []), newLeistungLohnFromArt(art)],
                          })
                        }
                      >
                        + {art.bezeichnung}
                      </Button>
                    ))
                  ) : null}
                </div>
              </div>
              <p className="mb-2 text-xs text-label-secondary">
                Lohnarten verwalten in{" "}
                <Link to="/admin/einstellungen/dokumente" className="text-accent hover:underline">
                  Einstellungen → Dokumente
                </Link>
                .
              </p>
              {(leistungKalk.lohn_liste || []).length === 0 ? (
                <p className="text-sm text-label-secondary">Noch kein Lohn erfasst.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="gd-kalk-table min-w-[980px]">
                    <thead>
                      <tr>
                        <th>Lohnart</th>
                        <th>Stunden</th>
                        <th>Stundensatz netto</th>
                        <th className="gd-kalk-col-internal">Interne Beschreibung</th>
                        <th
                          className="gd-kalk-col-bezug"
                          title="Einzel gilt je Stück, Gesamt einmal für die ganze Positionsmenge"
                        >
                          Bezug
                        </th>
                        <th>Summe netto</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(leistungKalk.lohn_liste || []).map((item, index) => (
                        <tr key={item.localId}>
                          <td>{item.bezeichnung}</td>
                          <td>
                            <input
                              className="eh-input w-24"
                              type="number"
                              value={item.stunden}
                              onChange={(e) =>
                                patchLeistungLohn(index, { stunden: Number(e.target.value) })
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td>
                            <input
                              className="eh-input w-28"
                              type="number"
                              value={item.stundensatz_netto}
                              onChange={(e) =>
                                patchLeistungLohn(index, {
                                  stundensatz_netto: Number(e.target.value),
                                })
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td className="gd-kalk-col-internal">
                            <input
                              className="eh-input min-w-[180px] gd-kalk-internal-input"
                              value={item.interne_beschreibung || ""}
                              onChange={(e) =>
                                patchLeistungLohn(index, { interne_beschreibung: e.target.value })
                              }
                              disabled={!editable}
                              placeholder="Intern …"
                            />
                          </td>
                          <td className="gd-kalk-col-bezug">
                            <PreisbezugControl
                              value={item.preisbezug}
                              disabled={!editable}
                              onChange={(preisbezug) => patchLeistungLohn(index, { preisbezug })}
                            />
                          </td>
                          <td>
                            <KalkZeileSumme
                              betrag={calcLeistungLohnZeileNetto(item)}
                              preisbezug={item.preisbezug}
                              positionsMenge={Number(active.menge) || 1}
                              currency={currency}
                            />
                          </td>
                          <td>
                            {editable ? (
                              <button
                                type="button"
                                className="text-xs text-status-orange"
                                onClick={() =>
                                  patchLeistung({
                                    lohn_liste: (leistungKalk.lohn_liste || []).filter((_, i) => i !== index),
                                  })
                                }
                              >
                                Entfernen
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GdPositionInternalSection>

            <GdPositionInternalSection
              title="Artikel in der Leistung"
              hint="Material wird intern kalkuliert. Einzel gilt je Stück, Gesamt einmal für die ganze Menge — z. B. Kurierkosten bei 200 Stück."
            >
              <div className="gd-pos-internal-block-head">
                <p className="text-xs text-label-secondary">
                  Material im EP: {formatCurrency(calcLeistungMaterialNetto(leistungKalk, active.menge), currency)} · EK:{" "}
                  {formatCurrency(calcLeistungEinkaufNetto(leistungKalk, active.menge), currency)}
                </p>
                {editable ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {dokumentTyp === "rechnung" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        title="Legt einen Artikel mit Leistungsbezeichnung, Einheit und Menge an. Den Preis trägst du danach ein."
                        onClick={() =>
                          patchLeistung({
                            artikel_liste: [
                              ...(leistungKalk.artikel_liste || []),
                              applyLeistungFieldsToArtikelZeile(newLeistungArtikel(), {
                                bezeichnung: active.bezeichnung,
                                einheit: active.einheit,
                                menge: active.menge,
                              }),
                            ],
                          })
                        }
                      >
                        Aus Leistung übernehmen
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        patchLeistung({
                          artikel_liste: [
                            ...(leistungKalk.artikel_liste || []),
                            newLeistungArtikel(),
                          ],
                        })
                      }
                    >
                      Artikel hinzufügen
                    </Button>
                  </div>
                ) : null}
              </div>
              {(leistungKalk.artikel_liste || []).length === 0 ? (
                <p className="text-sm text-label-secondary">Noch keine Artikel in dieser Leistung.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="gd-kalk-table gd-kalk-table--compact gd-kalk-table--artikel">
                    <thead>
                      <tr>
                        <th className="gd-kalk-col-menge">Menge</th>
                        <th className="gd-kalk-col-einheit">Einh.</th>
                        <th className="gd-kalk-col-bezeichnung">Bezeichnung</th>
                        <th className="gd-kalk-col-num">EK</th>
                        <th className="gd-kalk-col-num">Auf%</th>
                        <th className="gd-kalk-col-num">EP</th>
                        <th className="gd-kalk-col-num">Rab%</th>
                        <th
                          className="gd-kalk-col-bezug"
                          title="Einzel gilt je Stück, Gesamt einmal für die ganze Positionsmenge"
                        >
                          Bezug
                        </th>
                        <th className="gd-kalk-col-sum">Summe</th>
                        <th className="gd-kalk-col-action" aria-label="Aktionen" />
                      </tr>
                    </thead>
                    <tbody>
                      {(leistungKalk.artikel_liste || []).map((item, index) => (
                        <tr key={item.localId}>
                          <td className="gd-kalk-col-menge">
                            <input
                              className="eh-input gd-kalk-num-input"
                              type="number"
                              value={formatQuantityInput(item.menge)}
                              onChange={(e) =>
                                patchLeistungArtikel(index, { menge: Number(e.target.value) })
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td className="gd-kalk-col-einheit">
                            <UnitAutocompleteField
                              value={item.einheit || ""}
                              onChange={(value) => patchLeistungArtikel(index, { einheit: value })}
                              disabled={!editable}
                              className="gd-kalk-unit-field"
                            />
                          </td>
                          <td className="gd-kalk-col-bezeichnung">
                            <div className="gd-kalk-artikel-cell__row">
                              <div className="gd-kalk-artikel-cell__main">
                                <DocumentArticleField
                                  value={{
                                    bezeichnung: item.bezeichnung,
                                    artikel_id: item.artikel_id,
                                    hero_nr: item.hero_nr,
                                    einheit: item.einheit,
                                    ist_freier_artikel: item.ist_freier_artikel,
                                  }}
                                  disabled={!editable}
                                  placeholder="Artikel …"
                                  onApply={(result) => {
                                    if (
                                      result.preis_abgelaufen &&
                                      result.orientierungspreis_netto != null &&
                                      result.orientierungspreis_netto > 0
                                    ) {
                                      setLeistungOrientierung((prev) => ({
                                        ...prev,
                                        [item.localId]: {
                                          orientierungspreis_netto: result.orientierungspreis_netto!,
                                          preis_gueltig_bis: result.preis_gueltig_bis,
                                        },
                                      }));
                                    } else {
                                      setLeistungOrientierung((prev) => {
                                        const next = { ...prev };
                                        delete next[item.localId];
                                        return next;
                                      });
                                    }
                                    if (
                                      result.einkaufspreis_netto != null &&
                                      result.einzelpreis_netto != null
                                    ) {
                                      patchLeistungArtikel(
                                        index,
                                        applyLeistungArtikelPricingFromDatabase(
                                          { ...item, ...result },
                                          result.einkaufspreis_netto,
                                          result.einzelpreis_netto,
                                          result.aufschlag_prozent,
                                          { abgelaufen: Boolean(result.preis_abgelaufen) },
                                        ),
                                      );
                                      return;
                                    }
                                    patchLeistungArtikel(index, {
                                      bezeichnung: result.bezeichnung,
                                      artikel_id: result.artikel_id,
                                      hero_nr: result.hero_nr,
                                      einheit: result.einheit ?? item.einheit,
                                      ist_freier_artikel: result.ist_freier_artikel,
                                      einzelpreis_netto: result.einzelpreis_netto ?? item.einzelpreis_netto,
                                      ...(result.interne_beschreibung !== undefined
                                        ? { interne_beschreibung: result.interne_beschreibung }
                                        : {}),
                                    });
                                  }}
                                />
                                {(() => {
                                  const ori = leistungOrientierung[item.localId];
                                  if (!ori) return null;
                                  return (
                                    <p className="mt-1 text-[11px] leading-snug text-status-orange">
                                      Alter Preis:{" "}
                                      {formatCurrency(ori.orientierungspreis_netto, currency)}
                                      {ori.preis_gueltig_bis
                                        ? ` (bis ${new Date(
                                            `${ori.preis_gueltig_bis.slice(0, 10)}T12:00:00`,
                                          ).toLocaleDateString("de-DE")})`
                                        : ""}{" "}
                                      – nicht in Berechnung
                                    </p>
                                  );
                                })()}
                              </div>
                              <input
                                className="eh-input gd-kalk-artikel-cell__intern gd-kalk-internal-input"
                                value={item.interne_beschreibung || ""}
                                onChange={(e) =>
                                  patchLeistungArtikel(index, { interne_beschreibung: e.target.value })
                                }
                                disabled={!editable}
                                placeholder="Interne Beschreibung …"
                                aria-label="Interne Beschreibung"
                              />
                            </div>
                          </td>
                          <td className="gd-kalk-col-num">
                            <input
                              className="eh-input gd-kalk-num-input"
                              type="number"
                              value={item.einkaufspreis_netto ?? ""}
                              onChange={(e) =>
                                patchLeistungArtikel(
                                  index,
                                  syncLeistungArtikelPricing(
                                    item,
                                    "ek",
                                    e.target.value === "" ? 0 : Number(e.target.value),
                                  ),
                                )
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td className="gd-kalk-col-num">
                            <input
                              className="eh-input gd-kalk-num-input"
                              type="number"
                              value={item.aufschlag_prozent ?? ""}
                              onChange={(e) =>
                                patchLeistungArtikel(
                                  index,
                                  syncLeistungArtikelPricing(
                                    item,
                                    "aufschlag",
                                    e.target.value === "" ? 0 : Number(e.target.value),
                                  ),
                                )
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td className="gd-kalk-col-num">
                            <input
                              className="eh-input gd-kalk-num-input"
                              type="number"
                              value={item.einzelpreis_netto}
                              onChange={(e) =>
                                patchLeistungArtikel(
                                  index,
                                  syncLeistungArtikelPricing(item, "vk", Number(e.target.value)),
                                )
                              }
                              disabled={!editable || isLeistungArtikelEpFromDatabase(item)}
                              title={
                                isLeistungArtikelEpFromDatabase(item)
                                  ? "Aus Artikeldatenbank übernommen"
                                  : undefined
                              }
                            />
                          </td>
                          <td className="gd-kalk-col-num">
                            <input
                              className="eh-input gd-kalk-num-input"
                              type="number"
                              value={item.rabatt_prozent}
                              onChange={(e) =>
                                patchLeistungArtikel(index, {
                                  rabatt_prozent: Number(e.target.value),
                                })
                              }
                              disabled={!editable}
                            />
                          </td>
                          <td className="gd-kalk-col-bezug">
                            <PreisbezugControl
                              value={item.preisbezug}
                              disabled={!editable}
                              onChange={(preisbezug) => patchLeistungArtikel(index, { preisbezug })}
                            />
                          </td>
                          <td className="gd-kalk-col-sum whitespace-nowrap font-medium text-label">
                            <KalkZeileSumme
                              betrag={calcLeistungArtikelZeileNetto(item)}
                              preisbezug={item.preisbezug}
                              positionsMenge={Number(active.menge) || 1}
                              currency={currency}
                            />
                          </td>
                          <td className="gd-kalk-col-action">
                            {editable ? (
                              <button
                                type="button"
                                className="gd-kalk-remove-btn"
                                aria-label="Artikel entfernen"
                                title="Entfernen"
                                onClick={() =>
                                  patchLeistung({
                                    artikel_liste: (leistungKalk.artikel_liste || []).filter(
                                      (_, i) => i !== index,
                                    ),
                                  })
                                }
                              >
                                ✕
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GdPositionInternalSection>

            <GdPositionInternalSection
              title="Preiskalkulation"
              hint="Aus Material, Lohn und Aufschlag wird der Einzelpreis je Stück berechnet. Gesamtpreise werden durch die Positionsmenge geteilt. Eine Pauschale überschreibt das Ergebnis."
            >
              <div className="gd-leistung-pricing-grid">
                <div className="gd-leistung-pricing__controls">
                  <p className="gd-leistung-pricing__panel-title">Steuerung</p>
                  <TextInput
                    label="Aufschlag % (auf Material + Lohn)"
                    type="number"
                    value={String(leistungKalk.aufschlag_prozent ?? 0)}
                    onChange={(e) => patchLeistung({ aufschlag_prozent: Number(e.target.value) })}
                    disabled={!editable}
                  />
                  <div className="gd-pauschale-field">
                    <GermanDecimalInput
                      key={`${active.localId}-pauschale`}
                      label="Pauschale netto"
                      value={leistungPauschaleAktiv ? leistungKalk.pauschale_netto : 0}
                      onValueChange={(next) =>
                        patchLeistung(patchLeistungPauschale(leistungKalk, next))
                      }
                      disabled={!editable}
                      emptyWhenZero
                      placeholder="Optional — überschreibt kalkulierten EP"
                    />
                    {editable && leistungPauschaleAktiv ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => patchLeistung(patchLeistungPauschale(leistungKalk, 0))}
                      >
                        Pauschale entfernen
                      </Button>
                    ) : null}
                  </div>
                  <p className="gd-leistung-pricing__controls-hint">
                    Mit einer Pauschale legen Sie den EP im Dokument fest. Ohne Pauschale gilt der
                    kalkulierte Wert.
                  </p>
                </div>
                <div className="gd-leistung-pricing__breakdown">
                  <p className="gd-leistung-pricing__panel-title">Kalkulationskette</p>
                  <KalkSumStack className="gd-leistung-pricing__stack">
                    <KalkSumRow
                      label="Material netto"
                      value={calcLeistungMaterialNetto(leistungKalkLive, active.menge)}
                      currency={currency}
                    />
                    <KalkSumRow
                      label="Lohn netto"
                      value={calcLeistungLohnNetto(leistungKalkLive, active.menge)}
                      currency={currency}
                    />
                    <KalkSumRow
                      label="Zwischensumme netto"
                      value={calcLeistungZwischensummeNetto(leistungKalkLive, active.menge)}
                      currency={currency}
                    />
                    {Number(leistungKalkLive.aufschlag_prozent || 0) > 0 ? (
                      <KalkSumRow
                        label={`Aufschlag ${leistungKalkLive.aufschlag_prozent} %`}
                        value={calcLeistungAufschlagBetragNetto(leistungKalkLive, active.menge)}
                        currency={currency}
                      />
                    ) : null}
                    <KalkSumRow
                      label="Kalkulierter EP netto"
                      value={leistungKalkuliertNetto}
                      currency={currency}
                      bold
                      separator
                    />
                    {Math.abs(leistungVkDiff) >= 0.005 && leistungVkDriver === "pauschale" ? (
                      <KalkSumRow
                        label="Abweichung (Pauschale)"
                        value={leistungVkDiff}
                        currency={currency}
                        accent
                      />
                    ) : null}
                    <KalkSumRow
                      label="Gültiger EP netto"
                      value={leistungVkNetto}
                      currency={currency}
                      bold
                    />
                  </KalkSumStack>
                </div>
              </div>
            </GdPositionInternalSection>
          </>
        ) : null}

        {typ === "titel" ? (
          <GdPositionDocSection hint="Der Titel erscheint als Überschrift in der Positionsliste.">
            <TextInput
              label="Titel"
              value={active.bezeichnung}
              onChange={(e) => patch({ bezeichnung: e.target.value })}
              disabled={!editable}
            />
            <GdDocumentLangtextField
              label="Beschreibung (optional)"
              value={active.beschreibung || ""}
              onChange={(value) => patch({ beschreibung: value })}
              disabled={!editable}
            />
          </GdPositionDocSection>
        ) : null}

        {typ === "text" ? (
          <GdPositionDocSection hint="Der Text erscheint als freier Textblock im Dokument.">
            <TextInput
              label="Optionaler Titel"
              value={active.bezeichnung}
              onChange={(e) => patch({ bezeichnung: e.target.value })}
              disabled={!editable}
            />
            <GdDocumentLangtextField
              label="Text / Langtext"
              value={active.beschreibung || ""}
              onChange={(value) => patch({ beschreibung: value })}
              disabled={!editable}
            />
          </GdPositionDocSection>
        ) : null}

        {typ === "artikel" || typ === "leistung" || isNachweis ? (
          <GdPositionDocSection
            title="Bilder zur Position"
            hint="Erscheinen bei der Position im Angebot (Vorschau/PDF) und werden im Projekt-SharePoint abgelegt."
          >
            {active ? (
              <DocumentPositionImages
                docId={docId}
                auftragId={auftragId}
                position={active}
                editable={editable}
                onEnsureMediaKey={(row) => {
                  const next = ensurePositionMediaKey(row);
                  setDraft(next);
                  onSave(next);
                  return next;
                }}
                onAttachmentsChanged={onAttachmentsChanged}
                zIndexClass={zIndexClass}
              />
            ) : null}
          </GdPositionDocSection>
        ) : null}

        <GdPositionInternalSection
          title="Interne Notiz"
          hint="Nur für das Team sichtbar — erscheint nicht im Kundendokument."
        >
          <AutoResizeTextarea
            value={positionMeta.interne_notiz || ""}
            onChange={(e) => patchMeta({ interne_notiz: e.target.value })}
            disabled={!editable}
            minRows={2}
            placeholder={
              isNachweis
                ? "Interne Hinweise …"
                : "Notizen zur Nachkalkulation, interne Hinweise …"
            }
          />
        </GdPositionInternalSection>
      </div>
    </Dialog>
    <ConfirmActionDialog
      open={confirmStammSwitch}
      title="Zurück zur Artikeldatenbank?"
      confirmLabel="Artikelinformationen entfernen"
      cancelLabel="Abbrechen"
      variant="warning"
      zIndexClass="z-[450]"
      onCancel={() => setConfirmStammSwitch(false)}
      onConfirm={() => {
        setDraft((prev) => (prev ? clearArtikeldatenForStammSwitch(prev) : prev));
        setConfirmStammSwitch(false);
      }}
    >
      Beim Wechsel zur Artikeldatenbank werden alle Artikelinformationen dieser Position
      entfernt (Bezeichnung, Beschreibung, Preise, Einheit und Artikelnummer). Die Menge
      bleibt erhalten. Anschließend können Sie einen Artikel aus der Datenbank wählen.
    </ConfirmActionDialog>
    </>
  );
}
